/* ─────────────────────────────────────────────────────────────────────────
 * Cabinet — a portfolio as an arcade machine.
 *
 * An arcade screen set in a slate cabinet: a dithered sky with clouds
 * drifting across it, a floating island, and PORTFOLIO in fat banded block
 * letters that bob on springs and hop when the cursor runs over them. Two
 * joysticks stand in front of the glass and lean toward the cursor.
 *
 * PRESS ENTER and the résumé opens on the screen the way a game dialog does:
 * the frame draws out sideways, then down, in hard steps. Inside it is a
 * two-column character sheet — name, pixel portrait, contact on the left;
 * Experience / Projects / Education / Skills on the right. The arrow keys
 * drive it like a game menu, and the left stick physically tilts with every
 * press. The selected entry types its detail out like dialog text.
 *
 * THE TITLE IS NOT A FONT. It is a 5×7 alphabet doubled and grown a cell in
 * every direction (pixel.ts), then outlined, bevelled, hatched and banded
 * cell by cell into one small canvas per letter on resize. The frame loop
 * only blits those and moves them.
 *
 * TWO CANVASES, ONE CLOCK. The screen is painted at one fifth of its size
 * and scaled up with smoothing off, so every drift of a cloud is a whole
 * pixel step. The title is drawn at device resolution with its y offsets
 * snapped to half-cells, so motion is chunky without anything going soft.
 * Letters, joysticks and the stick-duck are all springs on the same
 * requestAnimationFrame. GSAP owns the entrance and the window's open/close.
 *
 * The section fills its parent (absolute, inset 0), so render it inside a
 * positioned element with a height. Its styles are the `.cab` block at the
 * end of src/styles/globals.css.
 * ───────────────────────────────────────────────────────────────────────── */

import type { CSSProperties, FC, ReactNode, Ref } from 'react'
import type { Entry, IconName } from '@/components/landing/cabinet/data'
import type { LetterSprite } from '@/components/landing/cabinet/pixel'

import {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState
} from 'react'

import { gsap } from '@/lib/animations/gsap-setup'
import { cn } from '@/lib/utils'
import { ICONS, PAL, RESUME } from '@/components/landing/cabinet/data'

import {
    ScreenScene,
    boldAdvanceCells,
    boldHeightCells,
    captionSprite,
    letterSprite,
    paintPortraitPhoto,
    paintPortraitSprite
} from '@/components/landing/cabinet/pixel'

interface CabinetProps {
    className?: string
}

interface PixelIconProps {
    name: IconName
}

interface SectionHeadProps {
    icon: IconName
    label: string
}

interface JoystickProps {
    refEl: Ref<SVGGElement>
    shadowEl: Ref<SVGGElement>
}

const TABS = [
    { id: 'experience', label: "Experience", items: RESUME.experience },
    { id: 'projects', label: "Projects", items: RESUME.projects },
    { id: 'education', label: "Education", items: RESUME.education },
    { id: 'skills', label: "Skills", items: [] as Entry[] }
] as const

const STICKS = ['stickL', 'stickR'] as const

// the palette reaches the stylesheet through these variables
const CAB_VARS = {
    '--cab-cream': PAL.cream,
    '--cab-cream-hi': PAL.creamHi,
    '--cab-plum': PAL.plum,
    '--cab-peach': PAL.peach,
    '--cab-tan': PAL.tan,
    '--cab-sage': PAL.sage,
    '--cab-teal': PAL.teal,
    '--cab-rust': PAL.rust
} as CSSProperties

type Spring = { x: number; v: number }

const stepSpring = (s: Spring, target: number, k: number, c: number, dt: number): void => {
    s.v += (-(s.x - target) * k - s.v * c) * dt
    s.x += s.v * dt
}

/* ── Small pieces ─────────────────────────────────────────────────────── */

const PixelIcon: FC<PixelIconProps> = ({ name }): ReactNode => {
    const rows = ICONS[name]
    return (
        <svg viewBox='0 0 11 11' className='cab-icon' shapeRendering='crispEdges' aria-hidden>
            {rows.flatMap((r, y) =>
                [...r].map((ch, x) =>
                    ch === 'k' ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} /> : null
                )
            )}
        </svg>
    )
}

const SectionHead: FC<SectionHeadProps> = ({ icon, label }): ReactNode => {
    return (
        <div className='cab-head' data-in>
            <span className='cab-head-icon'>
                <PixelIcon name={icon} />
            </span>
            <span className='cab-head-bar'>
                <span className='cab-head-text'>{label}</span>
            </span>
        </div>
    )
}

/* A real arcade stick, not a lollipop: a glossy ball lit from the room
 * with the cream CRT reflected in its gloss, a chrome shaft with a collar
 * where it meets the ball, and a black dust washer on the panel. Two shadows
 * sell the depth: a tight contact shadow under the washer that never moves,
 * and the ball's soft shadow on the panel, which slides as the stick tilts
 * (driven from the frame loop through `shadowEl`). */
const Joystick: FC<JoystickProps> = ({ refEl, shadowEl }): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9]/g, '')
    const u = (n: string) => `url(#${id}-${n})`
    return (
        <svg viewBox='0 0 200 270' className='cab-stick-svg' aria-hidden>
            <defs>
                <radialGradient id={`${id}-ball`} cx='0.38' cy='0.3' r='0.75'>
                    <stop offset='0' stopColor='#f19383' />
                    <stop offset='0.16' stopColor='#dc6553' />
                    <stop offset='0.5' stopColor='#bb4639' />
                    <stop offset='0.82' stopColor='#80291f' />
                    <stop offset='1' stopColor='#5a1a15' />
                </radialGradient>
                {/* the rim: light from the screen behind catches the upper edge */}
                <radialGradient id={`${id}-rim`} cx='0.5' cy='0.72' r='0.62'>
                    <stop offset='0.82' stopColor='#f9f3df' stopOpacity='0' />
                    <stop offset='1' stopColor='#f9f3df' stopOpacity='0.5' />
                </radialGradient>
                {/* bounce off the dark panel darkens the underside further */}
                <linearGradient id={`${id}-under`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0.55' stopColor='#1a0c0a' stopOpacity='0' />
                    <stop offset='1' stopColor='#1a0c0a' stopOpacity='0.45' />
                </linearGradient>
                <radialGradient id={`${id}-spec`} cx='0.5' cy='0.5' r='0.5'>
                    <stop offset='0' stopColor='#ffffff' stopOpacity='0.95' />
                    <stop offset='0.45' stopColor='#ffffff' stopOpacity='0.35' />
                    <stop offset='1' stopColor='#ffffff' stopOpacity='0' />
                </radialGradient>
                <linearGradient id={`${id}-screen`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#fbf4dc' stopOpacity='0.75' />
                    <stop offset='1' stopColor='#f3ebcf' stopOpacity='0.05' />
                </linearGradient>
                <linearGradient id={`${id}-chrome`} x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' stopColor='#15191c' />
                    <stop offset='0.28' stopColor='#5f686e' />
                    <stop offset='0.42' stopColor='#e3e8ea' />
                    <stop offset='0.55' stopColor='#7c858b' />
                    <stop offset='0.8' stopColor='#2d3338' />
                    <stop offset='1' stopColor='#111416' />
                </linearGradient>
                <linearGradient id={`${id}-shaftShade`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#000' stopOpacity='0.55' />
                    <stop offset='0.25' stopColor='#000' stopOpacity='0' />
                    <stop offset='0.85' stopColor='#000' stopOpacity='0' />
                    <stop offset='1' stopColor='#000' stopOpacity='0.5' />
                </linearGradient>
                <radialGradient id={`${id}-washer`} cx='0.5' cy='0.35' r='0.7'>
                    <stop offset='0' stopColor='#30363b' />
                    <stop offset='0.7' stopColor='#1b1f22' />
                    <stop offset='1' stopColor='#0d0f11' />
                </radialGradient>
                <linearGradient id={`${id}-washerLip`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0' stopColor='#0b0d0e' />
                    <stop offset='1' stopColor='#262b2f' />
                </linearGradient>
                <filter id={`${id}-soft`} x='-50%' y='-50%' width='200%' height='200%'>
                    <feGaussianBlur stdDeviation='7' />
                </filter>
                <filter id={`${id}-tight`} x='-50%' y='-50%' width='200%' height='200%'>
                    <feGaussianBlur stdDeviation='2.5' />
                </filter>
            </defs>

            {/* the ball's soft shadow on the panel — slides with the tilt */}
            <g ref={shadowEl}>
                <ellipse cx='108' cy='246' rx='58' ry='14' fill='#000' opacity='0.32' filter={u('soft')} />
            </g>
            {/* contact shadow + washer */}
            <ellipse cx='100' cy='243' rx='52' ry='10' fill='#000' opacity='0.55' filter={u('tight')} />
            <ellipse cx='100' cy='240' rx='48' ry='11.5' fill={u('washerLip')} />
            <ellipse cx='100' cy='237' rx='48' ry='11' fill={u('washer')} />
            <path d='M58 239 A48 11 0 0 0 142 239' fill='none' stroke='#4a5258' strokeWidth='1.2' opacity='0.8' />
            <ellipse cx='100' cy='235' rx='30' ry='6.5' fill='none' stroke='#0a0c0d' strokeWidth='1.5' opacity='0.7' />
            <ellipse cx='100' cy='234.5' rx='9' ry='2.6' fill='#050607' />

            <g ref={refEl} style={{ transformOrigin: '100px 234px' }}>
                {/* shaft */}
                <rect x='93' y='140' width='14' height='95' fill={u('chrome')} />
                <rect x='93' y='140' width='14' height='95' fill={u('shaftShade')} />
                {/* collar under the ball */}
                <ellipse cx='100' cy='146' rx='13' ry='4.5' fill='#1a1d20' />
                <rect x='88' y='140' width='24' height='6' fill='#23272b' />
                <ellipse cx='100' cy='140' rx='12' ry='4' fill='#3a4046' />

                {/* ball */}
                <circle cx='100' cy='92' r='56' fill={u('ball')} />
                <circle cx='100' cy='92' r='56' fill={u('under')} />
                <circle cx='100' cy='92' r='56' fill={u('rim')} />
                {/* the CRT, reflected: a soft cream window bent over the top of the ball */}
                <path
                    d='M66 62 C74 46 96 40 120 44 C130 46 136 52 134 58 C126 54 104 52 86 56 C76 58 70 62 66 62Z'
                    fill={u('screen')}
                />
                {/* the hot spot */}
                <ellipse cx='80' cy='66' rx='17' ry='10' fill={u('spec')} transform='rotate(-30 80 66)' />
                <ellipse cx='77' cy='64' rx='4.5' ry='3' fill='#fff' opacity='0.9' transform='rotate(-30 77 64)' />
            </g>
        </svg>
    )
}

/* ── The section ──────────────────────────────────────────────────────── */

const Cabinet: FC<CabinetProps> = ({ className = '' }): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const screenRef = useRef<HTMLDivElement>(null)
    const bgRef = useRef<HTMLCanvasElement>(null)
    const titleRef = useRef<HTMLCanvasElement>(null)
    const promptRef = useRef<HTMLButtonElement>(null)
    const stickLRef = useRef<SVGGElement>(null)
    const stickRRef = useRef<SVGGElement>(null)
    const shadowLRef = useRef<SVGGElement>(null)
    const shadowRRef = useRef<SVGGElement>(null)
    const sticksRef = useRef<HTMLDivElement>(null)
    const overlayRef = useRef<HTMLDivElement>(null)
    const windowRef = useRef<HTMLDivElement>(null)
    const portraitRef = useRef<HTMLCanvasElement>(null)

    const [open, setOpen] = useState(false)
    const [tab, setTab] = useState(0)
    const [sel, setSel] = useState(0)
    const [typing, setTyping] = useState({ key: '', n: 0 })
    const [credits, setCredits] = useState(0)

    // live state the frame loop + key handler read without re-subscribing
    const live = useRef({
        phase: 'closed' as 'closed' | 'opening' | 'open' | 'closing',
        visible: true,
        reduced: false,
        pointer: { x: -1e6, y: -1e6, inside: false },
        stickL: { tilt: { x: 0, v: 0 } as Spring, key: 0, keyUntil: 0, press: { x: 0, v: 0 } as Spring, pressUntil: 0 },
        stickR: { tilt: { x: 0, v: 0 } as Spring, key: 0, keyUntil: 0, press: { x: 0, v: 0 } as Spring, pressUntil: 0 },
        duck: { x: 0, v: 0 } as Spring,
        wave: 0 // a hop wave queued across the title (from a keypress)
    })

    /* ── Scene: resize, paint, loop ─────────────────────────────────────── */
    useEffect(() => {
        const root = rootRef.current!
        const screen = screenRef.current!
        const bg = bgRef.current!
        const tc = titleRef.current!
        const bctx = bg.getContext('2d')!
        const tctx = tc.getContext('2d')!
        const L = live.current
        L.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        let scene: ScreenScene | null = null
        let dpr = 1
        let W = 0
        let H = 0
        type Letter = LetterSprite & {
            x: number
            base: number
            y: Spring
            hot: boolean
            release: number
        }
        let letters: Letter[] = []
        let year: HTMLCanvasElement | null = null
        let role: HTMLCanvasElement | null = null
        let yearY = 0
        let roleY = 0
        let snap = 1
        let born = performance.now()

        const layout = () => {
            const r = screen.getBoundingClientRect()
            if (r.width < 2 || r.height < 2) return
            dpr = Math.min(window.devicePixelRatio || 1, 2)
            W = Math.round(r.width * dpr)
            H = Math.round(r.height * dpr)

            // the screen: one fifth resolution, scaled up hard
            const px = Math.max(3, Math.min(6, Math.round(r.width / 260)))
            const lw = Math.ceil(r.width / px)
            const lh = Math.ceil(r.height / px)
            bg.width = lw
            bg.height = lh
            bctx.imageSmoothingEnabled = false
            scene = new ScreenScene(lw, lh)

            // the title: fit the widest line into ~74% of the screen
            tc.width = W
            tc.height = H
            const word = RESUME.headline.toUpperCase()
            const CAP = 1.35
            const chars = [...word]
            const cells = chars.reduce(
                (s, ch, i) => s + boldAdvanceCells(ch) * (i === 0 || i === chars.length - 1 ? CAP : 1),
                0
            )
            const hCells = boldHeightCells('A')
            // portrait screens give the word more of the width; it is width-bound there
            const portrait = r.width < r.height
            let u = Math.floor((W * (portrait ? 0.88 : 0.74)) / cells)
            u = Math.min(u, Math.floor((H * 0.3) / (hCells * CAP)))
            u = Math.max(2, u)
            const uc = Math.round(u * CAP)
            snap = Math.max(1, Math.round(u / 2))

            const us = Math.max(2, Math.round(u * 0.8))
            const uy = Math.max(2, Math.round(u * 0.6))
            role = captionSprite(RESUME.role, us)
            year = captionSprite(RESUME.year, uy)

            const titleH = hCells * uc
            const gap = u * 3
            const block = year.height + gap + titleH + gap + role.height
            // short landscape screens (phones on their side) lose their lower third
            // to the joysticks, so the block rides higher there
            const centre = r.height < 460 && !portrait ? 0.36 : 0.42
            const top = Math.round(H * centre - block / 2)
            yearY = top
            const baseline = top + year.height + gap + titleH
            roleY = baseline + gap
            // the prompt hangs a fixed gap under the role, wherever the title lands
            const promptTop = (roleY + role.height) / dpr + Math.max(14, (u / dpr) * 4)
            screen.style.setProperty('--cab-prompt-top', `${Math.round(promptTop)}px`)

            // letters, bottom-aligned on one baseline, first and last set larger
            const widths = chars.map(
                (ch, i) => boldAdvanceCells(ch) * (i === 0 || i === chars.length - 1 ? uc : u)
            )
            let x = Math.round((W - widths.reduce((a, b) => a + b, 0)) / 2)
            const prev = letters
            letters = chars.map((ch, i) => {
                const uu = i === 0 || i === chars.length - 1 ? uc : u
                const spr = letterSprite(ch, uu)
                const l: Letter = {
                    ...spr,
                    x,
                    base: baseline,
                    y: prev[i]?.y ?? { x: L.reduced ? 0 : -H, v: 0 },
                    hot: false,
                    release: i * 0.07
                }
                x += widths[i]
                return l
            })
        }

        const ro = new ResizeObserver(() => layout())
        ro.observe(screen)
        layout()

        /* pointer — in screen-local device px */
        const onMove = (e: PointerEvent) => {
            const r = screen.getBoundingClientRect()
            L.pointer.x = (e.clientX - r.left) * dpr
            L.pointer.y = (e.clientY - r.top) * dpr
            L.pointer.inside = true
        }
        const onLeave = () => {
            L.pointer.inside = false
        }
        root.addEventListener('pointermove', onMove)
        root.addEventListener('pointerleave', onLeave)

        /* pause off-screen and in hidden tabs */
        const io = new IntersectionObserver(([e]) => {
            L.visible = e.isIntersecting
        })
        io.observe(root)

        let raf = 0
        let last = performance.now()
        const tick = (now: number) => {
            raf = requestAnimationFrame(tick)
            if (!L.visible || document.hidden || !scene) {
                last = now
                return
            }
            const dt = Math.min(0.05, (now - last) / 1000)
            last = now
            const t = (now - born) / 1000

            // screen
            scene.draw(bctx, dt, L.reduced ? 0 : 1)

            // title
            tctx.clearRect(0, 0, W, H)
            if (year) tctx.drawImage(year, Math.round((W - year.width) / 2), yearY)
            if (role) tctx.drawImage(role, Math.round((W - role.width) / 2), roleY)
            const P = L.pointer
            if (L.wave) {
                // a keypress sends a hop down the word
                const kick = L.wave
                letters.forEach((l, i) => {
                    l.release = Math.min(l.release, t)
                    setTimeout(() => (l.y.v -= snap * 2 * 26 * kick), i * 45)
                })
                L.wave = 0
            }
            letters.forEach((l, i) => {
                const gx = l.x
                const gy = l.base - l.gh
                if (t < l.release) return // still waiting to drop in
                const idle = L.reduced ? 0 : Math.sin(t * 2.1 - i * 0.55) * snap * 1.2
                stepSpring(l.y, idle, 190, 11, dt)
                const over =
                    P.inside && P.x > gx && P.x < gx + l.gw && P.y > gy - l.gh * 0.25 && P.y < gy + l.gh
                if (over && !l.hot && !L.reduced) l.y.v -= snap * 2 * 24
                l.hot = over
                const y = Math.round(l.y.x / snap) * snap
                tctx.drawImage(l.canvas, gx - l.ox, gy - l.oy + y)
            })

            // sticks lean toward the cursor, keys override, open ducks them
            const rootR = root.getBoundingClientRect()
            const screenR = screen.getBoundingClientRect()
            const ptrX = P.inside ? P.x / dpr + screenR.left - rootR.left : rootR.width / 2
            STICKS.forEach((k, i) => {
                const s = L[k]
                const sx = rootR.width * (i === 0 ? 0.3 : 0.7)
                let target = Math.max(-14, Math.min(14, ((ptrX - sx) / rootR.width) * 40))
                if (!P.inside) target = 0
                if (now < s.keyUntil) target = s.key * 26
                stepSpring(s.tilt, target, 260, 13, dt)
                stepSpring(s.press, now < s.pressUntil ? 1 : 0, 520, 20, dt)
            })
            stepSpring(L.duck, L.phase === 'closed' || L.phase === 'closing' ? 0 : 1, 140, 16, dt)
            const gl = stickLRef.current
            const gr = stickRRef.current
            if (gl) gl.style.transform = `translateY(${L.stickL.press.x * 14}px) rotate(${L.stickL.tilt.x}deg)`
            if (gr) gr.style.transform = `translateY(${L.stickR.press.x * 14}px) rotate(${L.stickR.tilt.x}deg)`
            // the ball sits ~140 units above the pivot; its shadow follows it over
            const sl = shadowLRef.current
            const sr = shadowRRef.current
            if (sl) sl.style.transform = `translateX(${Math.sin((L.stickL.tilt.x * Math.PI) / 180) * 80}px)`
            if (sr) sr.style.transform = `translateX(${Math.sin((L.stickR.tilt.x * Math.PI) / 180) * 80}px)`
            if (sticksRef.current) sticksRef.current.style.transform = `translateY(${L.duck.x * 58}%)`
        }
        raf = requestAnimationFrame(tick)

        /* entrance: the letters drop in on their springs; GSAP brings the rest */
        born = performance.now()
        const ctx = gsap.context(() => {
            if (L.reduced) return
            gsap.fromTo(
                root.querySelectorAll('[data-enter]'),
                { opacity: 0, y: 24, scale: 0.96, filter: 'blur(8px)' },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: 'blur(0px)',
                    duration: 1.2,
                    ease: 'power4.out',
                    stagger: 0.12,
                    delay: 0.5,
                    clearProps: 'filter'
                }
            )
        }, root)

        return () => {
            cancelAnimationFrame(raf)
            ro.disconnect()
            io.disconnect()
            root.removeEventListener('pointermove', onMove)
            root.removeEventListener('pointerleave', onLeave)
            ctx.revert()
        }
    }, [])

    /* ── Portrait ───────────────────────────────────────────────────────── */
    useEffect(() => {
        const c = portraitRef.current
        if (!c) return
        paintPortraitSprite(c)
        if (!RESUME.portraitSrc) return
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => paintPortraitPhoto(c, img)
        img.src = RESUME.portraitSrc
    }, [])

    /* ── Open / close ───────────────────────────────────────────────────── */
    const pulse = useCallback((which: 'stickL' | 'stickR', dir: number, press = false) => {
        const s = live.current[which]
        const now = performance.now()
        if (press) {
            s.pressUntil = now + 140
        } else {
            s.key = dir
            s.keyUntil = now + 200
        }
    }, [])

    const openWindow = useCallback(() => {
        const L = live.current
        if (L.phase !== 'closed') return
        L.phase = 'opening'
        pulse('stickR', 0, true)
        setCredits((c) => c + 1)
        setOpen(true)
    }, [pulse])

    const closeWindow = useCallback(() => {
        const L = live.current
        if (L.phase !== 'open') return
        L.phase = 'closing'
        pulse('stickR', 0, true)
        const ov = overlayRef.current!
        const win = windowRef.current!
        if (L.reduced) {
            gsap.set(ov, { autoAlpha: 0 })
            L.phase = 'closed'
            setOpen(false)
            promptRef.current?.focus({ preventScroll: true })
            return
        }
        gsap
            .timeline({
                onComplete: () => {
                    gsap.set(ov, { autoAlpha: 0 })
                    L.phase = 'closed'
                    setOpen(false)
                    promptRef.current?.focus({ preventScroll: true })
                }
            })
            .to(win.querySelectorAll('[data-in]'), { opacity: 0, duration: 0.12, ease: 'steps(2)' })
            .to(win, { scaleY: 0.02, duration: 0.2, ease: 'steps(4)' })
            .to(win, { scaleX: 0, duration: 0.18, ease: 'steps(4)' })
            .to(ov.querySelector('.cab-backdrop'), { opacity: 0, duration: 0.16, ease: 'steps(3)' }, '-=0.1')
    }, [pulse])

    // the open animation runs after the window has rendered
    useLayoutEffect(() => {
        const L = live.current
        if (!open || L.phase !== 'opening') return
        const ov = overlayRef.current!
        const win = windowRef.current!
        gsap.set(ov, { autoAlpha: 1 })
        const done = () => {
            L.phase = 'open'
            win.focus({ preventScroll: true })
        }
        if (L.reduced) {
            gsap.set(win, { scaleX: 1, scaleY: 1 })
            done()
            return
        }
        const tl = gsap
            .timeline({ onComplete: done })
            .fromTo(ov.querySelector('.cab-backdrop'), { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'steps(4)' })
            // the dialog draws out sideways, then down — in hard steps, like a game
            .fromTo(win, { scaleX: 0.04, scaleY: 0.02 }, { scaleX: 1, duration: 0.3, ease: 'steps(6)' }, 0.04)
            .to(win, { scaleY: 1, duration: 0.26, ease: 'steps(5)' })
            .fromTo(
                win.querySelectorAll('[data-in]'),
                { opacity: 0, y: 14, scale: 0.97, filter: 'blur(6px)' },
                { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.7, ease: 'power4.out', stagger: 0.04, clearProps: 'filter,transform' },
                '-=0.02'
            )
        return () => {
            tl.kill()
        }
    }, [open])

    /* ── Menu state ─────────────────────────────────────────────────────── */
    const items = TABS[tab].items
    const current = items[sel] as Entry | undefined

    const goTab = useCallback(
        (d: number) => {
            setTab((t) => (t + d + TABS.length) % TABS.length)
            setSel(0)
            pulse('stickL', d)
        },
        [pulse]
    )
    const goSel = useCallback(
        (d: number) => {
            const n = TABS[tab].items.length
            if (n) setSel((s) => (s + d + n) % n)
            pulse('stickL', 0, true)
        },
        [tab, pulse]
    )

    // dialog text: the selected entry's detail types itself out. The count is
    // keyed to the entry, so switching entries restarts it at 0 with no reset.
    const typeKey = `${open}:${tab}:${sel}`
    const full = current?.detail.length ?? 0
    const typed = typing.key === typeKey ? typing.n : 0
    useEffect(() => {
        if (!open || !full) return
        // reduced motion: the whole line lands on the first tick
        const step = live.current.reduced ? full : 2
        let n = 0
        const id = window.setInterval(() => {
            n = Math.min(n + step, full)
            setTyping({ key: typeKey, n })
            if (n >= full) window.clearInterval(id)
        }, 18)
        return () => window.clearInterval(id)
    }, [open, full, typeKey])

    /* ── Keys ───────────────────────────────────────────────────────────── */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return
            const t = e.target as HTMLElement | null
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
            const L = live.current
            if (!L.visible) return
            const onButton = !!t && (t.tagName === 'BUTTON' || t.tagName === 'A')
            const k = e.key

            if (L.phase === 'closed') {
                if ((k === 'Enter' || k === ' ') && !onButton) {
                    e.preventDefault()
                    openWindow()
                } else if (k === 'ArrowLeft') {
                    pulse('stickL', -1)
                    L.wave = 0.6
                } else if (k === 'ArrowRight') {
                    pulse('stickL', 1)
                    L.wave = 0.6
                }
                return
            }
            if (L.phase !== 'open') return
            if (k === 'Escape') {
                e.preventDefault()
                closeWindow()
            } else if (k === 'ArrowLeft' || k === 'a') {
                e.preventDefault()
                goTab(-1)
            } else if (k === 'ArrowRight' || k === 'd') {
                e.preventDefault()
                goTab(1)
            } else if (k === 'ArrowUp' || k === 'w') {
                e.preventDefault()
                goSel(-1)
            } else if (k === 'ArrowDown' || k === 's') {
                e.preventDefault()
                goSel(1)
            } else if (/^[1-4]$/.test(k)) {
                const i = Number(k) - 1
                pulse('stickL', i > tab ? 1 : -1)
                setTab(i)
                setSel(0)
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [openWindow, closeWindow, goTab, goSel, pulse, tab])

    /* ── Render ─────────────────────────────────────────────────────────── */
    const tabId = TABS[tab].id

    return (
        <div ref={rootRef} className={cn('cab', className)} style={CAB_VARS}>
            {/* the glass */}
            <div ref={screenRef} className='cab-screen'>
                <canvas ref={bgRef} className='cab-bg' aria-hidden />
                <canvas ref={titleRef} className='cab-title' aria-hidden />
                <h2 className='cab-sr'>
                    {RESUME.headline} {RESUME.year} — {RESUME.role}
                </h2>
                <button
                    ref={promptRef}
                    type='button'
                    className='cab-prompt'
                    data-enter
                    onClick={openWindow}
                    aria-haspopup='dialog'
                >
                    <span className='cab-blink cab-fine'>Press</span>
                    <kbd className='cab-key'>
                        <span className='cab-fine'>Enter</span>
                        <span className='cab-touch'>Tap</span>
                    </kbd>
                    <span className='cab-blink'>to continue</span>
                </button>
                <div className='cab-credit' aria-hidden>
                    Credit {String(credits).padStart(2, '0')}
                </div>
                <div className='cab-scan' aria-hidden />
                <div className='cab-glass' aria-hidden />
            </div>

            {/* the controls, in front of the glass */}
            <div ref={sticksRef} className='cab-sticks' data-enter>
                <button
                    type='button'
                    className='cab-stick cab-stick--l'
                    aria-label="Next section"
                    tabIndex={-1}
                    onClick={() => (live.current.phase === 'open' ? goTab(1) : openWindow())}
                >
                    <Joystick refEl={stickLRef} shadowEl={shadowLRef} />
                </button>
                <button
                    type='button'
                    className='cab-stick cab-stick--r'
                    aria-label={open ? "Close résumé" : "Open résumé"}
                    tabIndex={-1}
                    onClick={() => (live.current.phase === 'open' ? closeWindow() : openWindow())}
                >
                    <Joystick refEl={stickRRef} shadowEl={shadowRRef} />
                </button>
            </div>

            {/* the résumé window */}
            <div ref={overlayRef} className='cab-overlay' style={{ visibility: 'hidden', opacity: 0 }} aria-hidden={!open}>
                <div className='cab-backdrop' onClick={closeWindow} />
                <div className='cab-window-shadow'>
                    <div
                        ref={windowRef}
                        className='cab-window'
                        role='dialog'
                        aria-modal='true'
                        aria-label={`${RESUME.name} — résumé`}
                        tabIndex={-1}
                    >
                        <div className='cab-card'>
                            <button type='button' className='cab-close' onClick={closeWindow} aria-label="Close résumé" data-in>
                                <span aria-hidden>×</span>
                            </button>

                            {/* left: who */}
                            <aside className='cab-left'>
                                <h3 className='cab-name' data-in>
                                    {RESUME.name}
                                </h3>
                                <div className='cab-portrait' data-in>
                                    <canvas ref={portraitRef} aria-label={`Pixel portrait of ${RESUME.name}`} role='img' />
                                </div>
                                <div className='cab-block'>
                                    <SectionHead icon='contact' label="Contact" />
                                    <ul className='cab-list cab-list--contact' data-in>
                                        {RESUME.contact.map((c) => (
                                            <li key={c.label}>
                                                <span className='cab-bullet' aria-hidden />
                                                {c.href ? (
                                                    <a href={c.href} className='cab-link' target={c.href.startsWith('http') ? '_blank' : undefined} rel='noreferrer'>
                                                        {c.value}
                                                    </a>
                                                ) : (
                                                    <span className='cab-where'>{c.value}</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </aside>

                            {/* right: what */}
                            <section className='cab-right'>
                                <div className='cab-tabs' role='tablist' data-in>
                                    {TABS.map((tb, i) => (
                                        <button
                                            key={tb.id}
                                            type='button'
                                            role='tab'
                                            aria-selected={i === tab}
                                            className='cab-tab'
                                            onClick={() => {
                                                pulse('stickL', i > tab ? 1 : -1)
                                                setTab(i)
                                                setSel(0)
                                            }}
                                        >
                                            <span className='cab-tab-n'>{i + 1}</span>
                                            {tb.label}
                                        </button>
                                    ))}
                                </div>

                                <div className='cab-panel' role='tabpanel' key={tabId}>
                                    <SectionHead icon={tabId} label={TABS[tab].label} />

                                    {tabId !== 'skills' ? (
                                        <ol className='cab-list cab-timeline'>
                                            {items.map((it, i) => {
                                                const on = i === sel
                                                return (
                                                    <li key={it.title} className='cab-item' data-on={on || undefined} data-in>
                                                        <button
                                                            type='button'
                                                            className='cab-item-btn'
                                                            onClick={() => {
                                                                setSel(i)
                                                                pulse('stickL', 0, true)
                                                            }}
                                                            aria-expanded={on}
                                                        >
                                                            <span className='cab-caret' aria-hidden />
                                                            <span className='cab-bullet' aria-hidden />
                                                            <span className='cab-when'>{it.when}</span>
                                                            <span className='cab-what'>
                                                                {it.title}
                                                                <span className='cab-where'> · {it.where}</span>
                                                            </span>
                                                        </button>
                                                        {on && (
                                                            <div className='cab-detail'>
                                                                <p aria-label={it.detail}>
                                                                    <span aria-hidden>{it.detail.slice(0, typed)}</span>
                                                                    {typed < it.detail.length && <span className='cab-cursor' aria-hidden />}
                                                                </p>
                                                                {it.tags && typed >= it.detail.length && (
                                                                    <ul className='cab-tags'>
                                                                        {it.tags.map((tg) => (
                                                                            <li key={tg}>{tg}</li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        )}
                                                    </li>
                                                )
                                            })}
                                        </ol>
                                    ) : (
                                        <div className='cab-skills'>
                                            <ul className='cab-tiles' data-in>
                                                {RESUME.skills.map((s) => (
                                                    <li key={s.label} className={`cab-tile cab-tile--${s.tone}`}>
                                                        {s.label}
                                                    </li>
                                                ))}
                                            </ul>
                                            <ul className='cab-meters'>
                                                {RESUME.disciplines.map((d) => (
                                                    <li key={d.label} data-in>
                                                        <span className='cab-meter-label'>{d.label}</span>
                                                        <span className='cab-meter' role='img' aria-label={`${d.level} of 5`}>
                                                            {Array.from({ length: 5 }, (_, i) => (
                                                                <span key={i} data-full={i < d.level || undefined} />
                                                            ))}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                <p className='cab-hints' data-in aria-hidden>
                                    <span><kbd>←</kbd><kbd>→</kbd> section</span>
                                    <span><kbd>↑</kbd><kbd>↓</kbd> select</span>
                                    <span><kbd>Esc</kbd> close</span>
                                </p>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Cabinet
